import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faEdit,
  faTrash,
  faBuilding,
  faSearch,
  faClose,
  faSpinner,
  faCheckCircle,
  faCancel,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";

// Fetcher for SWR
const fetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.api+json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  return response.json();
};

export default function SupplierRegister() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showEditSupplierModal, setShowEditSupplierModal] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [newSupplierForm, setNewSupplierForm] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
  });

  const [editSupplierForm, setEditSupplierForm] = useState({
    id: null,
    name: "",
    address: "",
    phone: "",
    email: "",
  });

  // Fetch suppliers data
  const { data: supplierData, mutate: mutateSuppliers } = useSWR(
    `${process.env.REACT_APP_SUPPLIER_API}?sort=title&page[limit]=1000`,
    fetcher,
  );

  const fetchCsrfToken = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch CSRF token");
    }

    return await response.text();
  };

  // Filter suppliers
  const filteredSuppliers = () => {
    if (!supplierData?.data) return [];

    let filtered = supplierData.data.filter((supplier) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        supplier.attributes.title?.toLowerCase().includes(searchLower) ||
        supplier.attributes.field_address
          ?.toLowerCase()
          .includes(searchLower) ||
        supplier.attributes.field_phone?.toLowerCase().includes(searchLower) ||
        supplier.attributes.field_supplier_email
          ?.toLowerCase()
          .includes(searchLower)
      );
    });

    return filtered;
  };

  // Pagination
  const suppliers = filteredSuppliers();
  const totalPages = Math.ceil(suppliers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSuppliers = suppliers.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Function to add a new supplier
  const handleAddSupplier = async (e) => {
    e.preventDefault();

    if (!newSupplierForm.name.trim()) {
      setErrorMessage("Supplier name is required");
      setModalError(true);
      return;
    }

    setAddingSupplier(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setAddingSupplier(false);
      return;
    }

    const addSupplierPayload = {
      data: {
        type: "node--supplier",
        attributes: {
          title: newSupplierForm.name,
          field_address: newSupplierForm.address || "",
          field_phone: newSupplierForm.phone || "",
          field_supplier_email: newSupplierForm.email || "",
        },
      },
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_SUPPLIER_API}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(addSupplierPayload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Supplier created successfully:", result);

        if (mutateSuppliers) {
          mutateSuppliers();
        }

        setNewSupplierForm({
          name: "",
          address: "",
          phone: "",
          email: "",
        });
        setShowAddSupplierModal(false);
        setSuccessMessage("Supplier added successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Supplier creation error:", errorData);
        setErrorMessage(`Failed to create supplier: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error creating supplier:", err);
      setErrorMessage(
        err.message || "An error occurred while creating the supplier",
      );
      setModalError(true);
    }
    setAddingSupplier(false);
  };

  // Function to update supplier
  const handleUpdateSupplier = async (e) => {
    e.preventDefault();

    if (!editSupplierForm.name.trim()) {
      setErrorMessage("Supplier name is required");
      setModalError(true);
      return;
    }

    setLoading(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setLoading(false);
      return;
    }

    const updateSupplierPayload = {
      data: {
        type: "node--supplier",
        id: editSupplierForm.id,
        attributes: {
          title: editSupplierForm.name,
          field_address: editSupplierForm.address || "",
          field_phone: editSupplierForm.phone || "",
          field_supplier_email: editSupplierForm.email || "",
        },
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPPLIER_API}/${editSupplierForm.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(updateSupplierPayload),
        },
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Supplier updated successfully:", result);

        if (mutateSuppliers) {
          mutateSuppliers();
        }

        setShowEditSupplierModal(false);
        setEditSupplierForm({
          id: null,
          name: "",
          address: "",
          phone: "",
          email: "",
        });
        setSuccessMessage("Supplier updated successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Supplier update error:", errorData);
        setErrorMessage(`Failed to update supplier: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error updating supplier:", err);
      setErrorMessage(
        err.message || "An error occurred while updating the supplier",
      );
      setModalError(true);
    }
    setLoading(false);
  };

  // Function to delete supplier
  const handleDeleteSupplier = async (supplierId, supplierName) => {
    if (
      window.confirm(
        `Are you sure you want to delete supplier "${supplierName}"?`,
      )
    ) {
      let csrfToken;
      try {
        csrfToken = await fetchCsrfToken();
      } catch (error) {
        console.error("Failed to fetch CSRF token:", error);
        setErrorMessage("Authentication error. Please refresh the page.");
        setModalError(true);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_SUPPLIER_API}/${supplierId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/vnd.api+json",
              "X-CSRF-Token": csrfToken,
            },
            credentials: "include",
          },
        );

        if (response.ok) {
          setSuccessMessage("Supplier deleted successfully!");
          setModalSuccessMsg(true);
          mutateSuppliers();
        } else {
          const errorData = await response.json().catch(() => ({}));
          let errorMessageText = response.statusText;
          if (errorData.errors?.[0]?.detail) {
            errorMessageText = errorData.errors[0].detail;
          }
          setErrorMessage(`Failed to delete supplier: ${errorMessageText}`);
          setModalError(true);
        }
      } catch (err) {
        console.error("Error deleting supplier:", err);
        setErrorMessage("An error occurred while deleting the supplier");
        setModalError(true);
      }
    }
  };

  // Calculate statistics
  const calculateStatistics = () => {
    if (!supplierData?.data) return { totalSuppliers: 0 };

    return { totalSuppliers: supplierData.data.length };
  };

  const { totalSuppliers } = calculateStatistics();

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="suppliers-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Supplier Register"
            buttonLabel="Add Supplier"
            icon={faAdd}
            onClick={() => setShowAddSupplierModal(true)}
          />

          {/* STATISTICS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faBuilding} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{totalSuppliers}</h3>
                <p style={styles.statLabel}>Total Suppliers</p>
              </div>
            </div>
          </div>

          {/* SEARCH BAR */}
          <div style={styles.searchBar}>
            <div style={styles.searchContainer}>
              <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search suppliers by name, address, phone or email..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* SUPPLIERS LIST TABLE */}
          <div style={styles.card}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Supplier Name</th>
                    <th>Address</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!supplierData ? (
                    <tr>
                      <td
                        colSpan="5"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading
                        suppliers...
                      </td>
                    </tr>
                  ) : paginatedSuppliers.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        {searchTerm
                          ? "No suppliers match your search"
                          : "No suppliers found"}
                      </td>
                    </tr>
                  ) : (
                    paginatedSuppliers.map((supplier) => (
                      <tr key={supplier.id}>
                        <td>
                          <div style={styles.supplierNameCell}>
                            {supplier.attributes.title}
                          </div>
                        </td>
                        <td>
                          {supplier.attributes.field_address ? (
                            <span>{supplier.attributes.field_address}</span>
                          ) : (
                            "--"
                          )}
                        </td>
                        <td>
                          {supplier.attributes.field_phone ? (
                            <span>{supplier.attributes.field_phone}</span>
                          ) : (
                            "--"
                          )}
                        </td>
                        <td>
                          {supplier.attributes.field_supplier_email ? (
                            <span>
                              {supplier.attributes.field_supplier_email}
                            </span>
                          ) : (
                            "--"
                          )}
                        </td>
                        <td style={styles.actionBTN}>
                          <button
                            style={styles.editButton}
                            onClick={() => {
                              setEditSupplierForm({
                                id: supplier.id,
                                name: supplier.attributes.title || "",
                                address:
                                  supplier.attributes.field_address || "",
                                phone: supplier.attributes.field_phone || "",
                                email:
                                  supplier.attributes.field_supplier_email ||
                                  "",
                              });
                              setShowEditSupplierModal(true);
                            }}
                            title="Edit Supplier"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            style={styles.deleteButton}
                            onClick={() =>
                              handleDeleteSupplier(
                                supplier.id,
                                supplier.attributes.title,
                              )
                            }
                            title="Delete Supplier"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Add Supplier Modal */}
            {showAddSupplierModal && (
              <div className="modal">
                <div className="overlay"></div>
                <div className="modal-content" style={{ maxWidth: "500px" }}>
                  <div className="modal-body">
                    <div className="modal-header">
                      <p>Add New Supplier</p>
                      <button
                        className="close-modal"
                        onClick={() => {
                          setShowAddSupplierModal(false);
                          setNewSupplierForm({
                            name: "",
                            address: "",
                            phone: "",
                            email: "",
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>

                    <form
                      onSubmit={handleAddSupplier}
                      className="edit-patient-form"
                    >
                      <div className="form-group">
                        <label>Supplier Name *</label>
                        <input
                          type="text"
                          placeholder="Enter Supplier Name"
                          value={newSupplierForm.name}
                          onChange={(e) =>
                            setNewSupplierForm({
                              ...newSupplierForm,
                              name: e.target.value,
                            })
                          }
                          required
                          autoFocus
                        />
                      </div>

                      <div className="form-group">
                        <label>Address</label>
                        <input
                          type="text"
                          placeholder="Enter Address"
                          value={newSupplierForm.address}
                          onChange={(e) =>
                            setNewSupplierForm({
                              ...newSupplierForm,
                              address: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div style={styles.formgroupwrapper}>
                        <div className="form-group">
                          <label>Phone</label>
                          <input
                            type="text"
                            placeholder="Enter Phone Number"
                            value={newSupplierForm.phone}
                            onChange={(e) =>
                              setNewSupplierForm({
                                ...newSupplierForm,
                                phone: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            placeholder="Enter Email Address"
                            value={newSupplierForm.email}
                            onChange={(e) =>
                              setNewSupplierForm({
                                ...newSupplierForm,
                                email: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="submitGroup">
                        <button
                          type="submit"
                          disabled={addingSupplier}
                          style={styles.button}
                        >
                          {addingSupplier ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Add Supplier"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Supplier Modal */}
            {showEditSupplierModal && (
              <div className="modal">
                <div className="overlay"></div>
                <div className="modal-content" style={{ maxWidth: "500px" }}>
                  <div className="modal-body">
                    <div className="modal-header">
                      <p>Edit Supplier</p>
                      <button
                        className="close-modal"
                        onClick={() => {
                          setShowEditSupplierModal(false);
                          setEditSupplierForm({
                            id: null,
                            name: "",
                            address: "",
                            phone: "",
                            email: "",
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>

                    <form
                      onSubmit={handleUpdateSupplier}
                      className="edit-patient-form"
                    >
                      <div className="form-group">
                        <label>Supplier Name *</label>
                        <input
                          type="text"
                          placeholder="Enter Supplier Name"
                          value={editSupplierForm.name}
                          onChange={(e) =>
                            setEditSupplierForm({
                              ...editSupplierForm,
                              name: e.target.value,
                            })
                          }
                          required
                          autoFocus
                        />
                      </div>

                      <div className="form-group">
                        <label>Address</label>
                        <input
                          type="text"
                          placeholder="Enter Address"
                          value={editSupplierForm.address}
                          onChange={(e) =>
                            setEditSupplierForm({
                              ...editSupplierForm,
                              address: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div style={styles.formgroupwrapper}>
                        <div className="form-group">
                          <label>Phone</label>
                          <input
                            type="text"
                            placeholder="Enter Phone Number"
                            value={editSupplierForm.phone}
                            onChange={(e) =>
                              setEditSupplierForm({
                                ...editSupplierForm,
                                phone: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            placeholder="Enter Email Address"
                            value={editSupplierForm.email}
                            onChange={(e) =>
                              setEditSupplierForm({
                                ...editSupplierForm,
                                email: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="submitGroup">
                        <button
                          type="submit"
                          disabled={loading}
                          style={styles.button}
                        >
                          {loading ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Update Supplier"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        style={{
                          ...styles.pageButton,
                          ...(currentPage === pageNum
                            ? styles.activePageButton
                            : {}),
                        }}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <div style={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faCancel}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    width: "100%",
    marginTop: "20px",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px",
  },
  searchBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    gap: "20px",
  },
  searchContainer: {
    flex: 1,
    position: "relative",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#666",
  },
  searchInput: {
    width: "100%",
    padding: "12px 12px 12px 40px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(1, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  statIcon: {
    fontSize: "22px",
    color: "#007bff",
  },
  statValue: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "700",
    color: "#333",
  },
  statLabel: {
    margin: "5px 0 0 0",
    color: "#666",
    fontSize: "14px",
  },
  button: {
    background: "#333",
    color: "white",
    padding: "8px 24px",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  formgroupwrapper: {
    display: "flex",
    gap: "10px",
  },
  actionBTN: {
    display: "flex",
    gap: "8px",
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e0e0e0",
  },
  paginationButton: {
    background: "#f8f9fa",
    border: "1px solid #dee2e6",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  pageNumbers: {
    display: "flex",
    gap: "8px",
  },
  pageButton: {
    background: "white",
    border: "1px solid #dee2e6",
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    minWidth: "40px",
  },
  activePageButton: {
    background: "#007bff",
    color: "white",
    borderColor: "#007bff",
  },
  pageInfo: {
    color: "#666",
    fontSize: "14px",
  },
  supplierNameCell: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  supplierIcon: {
    color: "#007bff",
    fontSize: "16px",
  },
  fieldIcon: {
    marginRight: "8px",
    color: "#666",
    fontSize: "12px",
  },
};
